import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Snackbar, Stack, TextField } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import {
  useGetSalePTBFFixationPrintEnOptionsQuery,
  useGetSalePTBFFixationPrintVnOptionsQuery,
  usePrintSalePTBFFixationEnMutation,
  usePrintSalePTBFFixationVnMutation,
  type SalePTBFFixation,
} from '../../store/api/salePTBFFixationsApi'

interface SalePTBFFixationPrintDialogProps {
  open: boolean
  language: 'en' | 'vn'
  fixation: SalePTBFFixation | null
  onClose: () => void
  canEdit?: boolean
}

const PRINT_FIXATION_PREVIEW_CSS = `
@page { size: A4; margin: 0; }
.print-action { display: none !important; }
.report-page { max-width: none !important; }
`.trim()

function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}

function buildPreviewHtml(rawHtml: string): string {
  const htmlWithoutInlinePrint = rawHtml.replace(/<button\s+class="print-action[^>]*>[\s\S]*?<\/button>/g, '')
  const withBase = htmlWithoutInlinePrint.replace('<head>', `<head><base href="${window.location.origin}/">`)
  const styleTag = `<style id="sale-ptbf-fixation-preview-overrides">${PRINT_FIXATION_PREVIEW_CSS}</style>`
  if (withBase.includes('</head>')) return withBase.replace('</head>', `${styleTag}</head>`)
  return `${styleTag}${withBase}`
}

export default function SalePTBFFixationPrintDialog({ open, language, fixation, onClose, canEdit = true }: SalePTBFFixationPrintDialogProps) {
  const [previewHtml, setPreviewHtml] = useState('')
  const [toastMessage, setToastMessage] = useState('')
  const [manualFxRoe, setManualFxRoe] = useState<string>('')
  const [manualNote, setManualNote] = useState('')
  const { data: enOptions, isFetching: isEnFetching } = useGetSalePTBFFixationPrintEnOptionsQuery(fixation?.contractNo ?? '', {
    skip: !open || language !== 'en' || !fixation,
  })
  const { data: vnOptions, isFetching: isVnFetching } = useGetSalePTBFFixationPrintVnOptionsQuery(fixation?.contractNo ?? '', {
    skip: !open || language !== 'vn' || !fixation,
  })
  const isOptionsFetching = language === 'en' ? isEnFetching : isVnFetching
  const [printEn, { isLoading: isPrintingEn }] = usePrintSalePTBFFixationEnMutation()
  const [printVn, { isLoading: isPrintingVn }] = usePrintSalePTBFFixationVnMutation()
  const isPrinting = isPrintingEn || isPrintingVn

  const options = useMemo(() => (language === 'en' ? enOptions : vnOptions), [enOptions, language, vnOptions])

  useEffect(() => {
    if (!open) return
    setManualFxRoe(options?.fxRoe != null ? String(options.fxRoe) : fixation?.fxRoe != null ? String(fixation.fxRoe) : '')
    setManualNote(options?.note ?? '')
  }, [fixation?.fxRoe, open, options])

  const handlePreview = async () => {
    if (!fixation) return
    const result =
      language === 'vn'
        ? await printVn({
            contractNo: fixation.contractNo,
            body: {
              fxRoe: manualFxRoe === '' ? null : Number(manualFxRoe),
              note: manualNote || null,
            },
          }).unwrap()
        : await printEn({
            contractNo: fixation.contractNo,
            body: {
              fxRoe: manualFxRoe === '' ? null : Number(manualFxRoe),
              note: manualNote || null,
            },
          }).unwrap()
    setPreviewHtml(buildPreviewHtml(result.html))
    setToastMessage(result.message ?? 'Print preview generated.')
    onClose()
  }

  const title = 'Choose Value'
  const subtitle = 'Input Value for Print Form'
  const iframeTitle = language === 'vn' ? 'Sale PTBF Fixation VN Preview' : 'Sale PTBF Fixation EN Preview'

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Box>
              <Box sx={{ fontWeight: 700, fontSize: '0.875rem', mb: 0.25 }}>{subtitle}</Box>
            </Box>
            <TextField label="Contract No" size="small" fullWidth value={fixation?.contractNo ?? ''} InputProps={{ readOnly: true }} />
            <TextField
              label="ROE"
              size="small"
              fullWidth
              value={manualFxRoe}
              onChange={(e) => setManualFxRoe(e.target.value)}
              disabled={!canEdit}
            />
            <TextField
              label="Note"
              size="small"
              fullWidth
              multiline
              minRows={4}
              value={manualNote}
              onChange={(e) => setManualNote(e.target.value)}
              disabled={!canEdit}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              <Button size="small" onClick={onClose}>
                Cancel
              </Button>
              <Box sx={{ position: 'relative' }}>
                <Button size="small" variant="contained" onClick={handlePreview} disabled={!fixation || isPrinting || isOptionsFetching}>
                  Print
                </Button>
                {(isPrinting || isOptionsFetching) && (
                  <CircularProgress size={20} sx={{ position: 'absolute', top: '50%', left: '50%', mt: '-10px', ml: '-10px' }} />
                )}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(previewHtml)} onClose={() => setPreviewHtml('')} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box component="span">{title}</Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setPreviewHtml('')} size="small">
              Close
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                const frame = document.querySelector<HTMLIFrameElement>(`iframe[title="${iframeTitle}"]`)
                const originalTitle = document.title
                document.title = `${sanitizePdfFileNamePart(fixation?.contractNo ?? 'ptbf-fixation')}_PTBF_Fixation_${language.toUpperCase()}.pdf`
                if (frame?.contentDocument) frame.contentDocument.title = document.title
                frame?.contentWindow?.focus()
                frame?.contentWindow?.print()
                window.setTimeout(() => {
                  document.title = originalTitle
                }, 1000)
              }}
            >
              Print
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2000}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  )
}
